import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { portalFetch } from "../api/client";
import type { DiscoverySyncState } from "../api/discoveryLive";
import type {
	PortalArtifactDto,
	PortalDiscoverySnapshotDto,
	PortalSpecReadinessDto,
	PortalWorkMapDto,
	PortalWorkTicketDto,
} from "../model";
import { ConversationView } from "./ConversationView";
import { MessageBody } from "./MessageBody";
import { PrototypeViewer } from "./PrototypeViewer";
import { RichText } from "./RichText";
import { StatusBanner } from "./StatusBanner";

const participationLabel = {
	human: "Needs your judgment",
	agent: "Agent investigating",
} as const;

export type WayfinderPane = "tasks" | "chat" | "details";

export function DiscoveryWorkspace({
	projectId,
	maps,
	selectedMapId,
	activePane,
	onActivePaneChange,
	onMapsUpdated,
	voiceAvailable,
	agentModel,
	readiness,
	discoverySnapshot,
	syncState,
	onSpecUpdated,
	mobileProjectMenu,
	initialMessage,
	onInitialMessageSent,
}: {
	projectId: string;
	maps: PortalWorkMapDto[];
	selectedMapId: string;
	activePane: WayfinderPane;
	onActivePaneChange: (pane: WayfinderPane) => void;
	onMapsUpdated: () => Promise<void>;
	voiceAvailable?: boolean;
	agentModel: string;
	readiness: PortalSpecReadinessDto;
	discoverySnapshot?: PortalDiscoverySnapshotDto;
	syncState: DiscoverySyncState;
	onSpecUpdated?: () => Promise<void>;
	mobileProjectMenu?: ReactNode;
	initialMessage?: { key: string; message: string };
	onInitialMessageSent?: () => void;
}) {
	const [status, setStatus] = useState("");
	const [selectedId, setSelectedId] = useState<string>();
	const [saving, setSaving] = useState(false);
	const [draftRequest, setDraftRequest] = useState<{ key: string; message: string }>();
	const [evidence, setEvidence] = useState<{ artifactId: string; name: string; content: string }>();
	const [loadingEvidence, setLoadingEvidence] = useState<string>();

	const map = maps.find((item) => item.id === selectedMapId) ?? maps[0];
	useEffect(() => {
		if (!map) return setSelectedId(undefined);
		setSelectedId((existing) =>
			existing && map.tickets.some((ticket) => ticket.id === existing)
				? existing
				: (map.tickets.find((ticket) => ticket.isFrontier && ticket.participation === "human")?.id ??
					map.tickets.find((ticket) => ticket.isFrontier)?.id),
		);
	}, [map]);
	const refreshAfterAgent = useCallback(async () => {
		await onMapsUpdated();
		await onSpecUpdated?.();
	}, [onMapsUpdated, onSpecUpdated]);

	const selected = map?.tickets.find((ticket) => ticket.id === selectedId);
	const ticketById = useMemo(() => new Map((map?.tickets ?? []).map((ticket) => [ticket.id, ticket])), [map]);
	const prototypes = useMemo(() => {
		const seen = new Set<string>();
		return (map?.tickets ?? []).flatMap((ticket) =>
			ticket.artifacts
				.filter((artifact) => artifact.role === "prototype" && !seen.has(artifact.artifactId))
				.map((artifact) => {
					seen.add(artifact.artifactId);
					return {
						artifactId: artifact.artifactId,
						name: artifact.name,
						ticketTitle: ticket.question ?? ticket.title,
					};
				}),
		);
	}, [map]);
	const domains = useMemo(() => {
		if (!map) return [];
		const active = map.domains.filter((domain) => domain.status === "active");
		const groups = active
			.sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name))
			.map((domain) => ({
				...domain,
				tickets: map.tickets.filter((ticket) => ticket.domainId === domain.id).sort(ticketOrder),
			}));
		const unassigned = map.tickets.filter(
			(ticket) => !ticket.domainId || !active.some((d) => d.id === ticket.domainId),
		);
		const organized = unassigned.length
			? [
					...groups,
					{
						id: "unassigned",
						name: "Project shape",
						sortOrder: 999,
						status: "active" as const,
						createdAt: "",
						updatedAt: "",
						tickets: unassigned.sort(ticketOrder),
					},
				]
			: groups;
		return organized.length
			? organized
			: [
					{
						id: "mapping",
						name: "Project terrain",
						summary: "The agent is mapping the first stable domains.",
						sortOrder: 0,
						status: "active" as const,
						createdAt: "",
						updatedAt: "",
						tickets: [],
					},
				];
	}, [map]);

	function requestDraft(message: string) {
		setDraftRequest({ key: crypto.randomUUID(), message });
	}

	async function deleteQuestion() {
		if (!map || !selected || saving) return;
		if (!window.confirm("Delete this unanswered question from the Wayfinder map?")) return;
		setSaving(true);
		try {
			await portalFetch(`/api/portal/v1/projects/${projectId}/work-maps/${map.id}/tickets/${selected.id}`, {
				method: "DELETE",
			});
			setSelectedId(undefined);
			setStatus("Question deleted from the Wayfinder map.");
			await onMapsUpdated();
		} catch {
			setStatus("The question could not be deleted. Nothing was changed.");
		} finally {
			setSaving(false);
		}
	}

	async function readEvidence(artifactId: string) {
		setLoadingEvidence(artifactId);
		try {
			const artifact = await portalFetch<PortalArtifactDto>(
				`/api/portal/v1/projects/${projectId}/artifacts/${artifactId}`,
			);
			setEvidence({
				artifactId,
				name: artifact.name,
				content: artifact.content ?? "No readable evidence was returned.",
			});
		} catch {
			setStatus("The linked research evidence could not be opened.");
		} finally {
			setLoadingEvidence(undefined);
		}
	}

	const currentRevision = map?.destinationRevisions[0]?.revision ?? 1;
	const selectedLabel = selected?.question ?? selected?.title;
	const selectedDomain = selected
		? domains.find((domain) => domain.tickets.some((ticket) => ticket.id === selected.id))
		: undefined;
	return (
		<div className="discovery-page">
			<div className="wayfinder-shell-chrome">
				<div className="mobile-chat-toolbar">
					{mobileProjectMenu}
					<nav className="wayfinder-pane-tabs" aria-label="Wayfinder panes">
						{(["tasks", "chat", "details"] as const).map((pane) => (
							<button
								key={pane}
								type="button"
								className={activePane === pane ? "active" : undefined}
								aria-current={activePane === pane ? "page" : undefined}
								onClick={() => onActivePaneChange(pane)}
							>
								{pane === "tasks" ? "Tasks" : pane === "chat" ? "Chat" : "Details"}
							</button>
						))}
					</nav>
				</div>
				{status && <StatusBanner kind={status.includes("could not") ? "error" : "info"}>{status}</StatusBanner>}
				{!map && <p className="compact-empty-state">The Wayfinder map is unavailable.</p>}
			</div>
			<div className="wayfinder-pane-grid" data-active-pane={activePane}>
				<nav className="wayfinder-pane wayfinder-tasks-pane" aria-labelledby="wayfinder-tasks-heading">
					<header className="wayfinder-pane-heading">
						<div>
							<span>Tasks</span>
							<h3 id="wayfinder-tasks-heading">Map navigation</h3>
						</div>
						{map && <strong>{map.counts.frontier} current</strong>}
					</header>
					{map && (
						<>
							<section className="wayfinder-current-map" aria-label="Current map">
								<span>Current destination · revision {currentRevision}</span>
								<strong>{map.title}</strong>
								<p>{map.destination}</p>
								<div className="atlas-counts" role="status" aria-label="Map status">
									<span>
										<strong>{domains.length}</strong> domains
									</span>
									<span>
										<strong>{map.counts.frontier}</strong> current
									</span>
									<span>
										<strong>{map.counts.resolved}</strong> decided
									</span>
								</div>
								<button
									type="button"
									className="secondary"
									onClick={() => {
										requestDraft(
											`For Wayfinder map “${map.title}” (${map.id}), I want to refine our destination. Current destination: ${map.destination}\n\nNew direction: `,
										);
										onActivePaneChange("chat");
									}}
								>
									Steer destination
								</button>
							</section>
							<section className="domain-atlas" aria-label="Tasks for the selected map">
								<header className="atlas-map-heading">
									<div>
										<span>Selected map tasks</span>
										<h3>{selectedLabel ? <RichText content={selectedLabel} inline /> : "Choose a task"}</h3>
									</div>
									<div className="atlas-legend" role="list" aria-label="Task legend">
										<span role="listitem" className="frontier">
											Current
										</span>
										<span role="listitem" className="waiting">
											Waiting
										</span>
										<span role="listitem" className="decided">
											Decided
										</span>
									</div>
								</header>
								<div className="domain-islands">
									{domains.map((domain, index) => (
										<section key={domain.id} className="domain-island">
											<header>
												<span>{String(index + 1).padStart(2, "0")}</span>
												<div>
													<h4>{domain.name}</h4>
													{"summary" in domain && domain.summary && <p>{domain.summary}</p>}
												</div>
												<small>
													{domain.tickets.filter((ticket) => ticket.status === "resolved").length}/
													{domain.tickets.length}
												</small>
											</header>
											{domain.tickets.length ? (
												<ul>
													{domain.tickets.map((ticket) => (
														<li key={ticket.id}>
															<TicketNode
																ticket={ticket}
																selected={ticket.id === selectedId}
																onSelect={(id) => {
																	setSelectedId(id);
																	onActivePaneChange("details");
																}}
															/>
														</li>
													))}
												</ul>
											) : (
												<p className="domain-empty">The agent is still mapping this domain.</p>
											)}
										</section>
									))}
								</div>
							</section>
						</>
					)}
				</nav>
				<section className="wayfinder-pane wayfinder-chat-pane">
					<ConversationView
						initialMessage={initialMessage}
						onInitialMessageSent={onInitialMessageSent}
						projectId={projectId}
						mapId={map?.id}
						voiceAvailable={voiceAvailable}
						agentModel={agentModel}
						readiness={readiness}
						discoverySnapshot={discoverySnapshot}
						syncState={syncState}
						onSpecUpdated={refreshAfterAgent}
						draftRequest={draftRequest}
						prototypes={prototypes}
					/>
				</section>
				<aside className="wayfinder-pane wayfinder-details-pane" aria-labelledby="wayfinder-details-heading">
					<header className="wayfinder-pane-heading">
						<div>
							<span>Details</span>
							<h3 id="wayfinder-details-heading">{selected ? "Selected task" : "No task selected"}</h3>
						</div>
						{selected && <strong>{ticketState(selected)}</strong>}
					</header>
					{map && selected ? (
						<>
							<section className="atlas-selection" aria-live="polite">
								<div className="atlas-detail">
									<div>
										<span className="ticket-owner">{participationLabel[selected.participation]}</span>
										{selectedDomain && <p className="ticket-domain">{selectedDomain.name}</p>}
										<h3>
											<RichText content={selected.question ?? selected.title} inline />
										</h3>
										{selected.description && <MessageBody content={selected.description} markdown />}
									</div>
									<div className="atlas-path">
										<span>Task path</span>
										<div>
											{selected.blockedByIds
												.map((id) => ticketById.get(id))
												.filter(Boolean)
												.map((ticket) => (
													<PathNode key={ticket?.id} ticket={ticket as PortalWorkTicketDto} />
												))}
											<PathNode ticket={selected} active />
											{selected.blockingIds
												.map((id) => ticketById.get(id))
												.filter(Boolean)
												.slice(0, 2)
												.map((ticket) => (
													<PathNode key={ticket?.id} ticket={ticket as PortalWorkTicketDto} />
												))}
										</div>
									</div>
									{selected.resolution ? (
										<div className="ticket-resolution">
											<strong>Decision</strong>
											<MessageBody content={selected.resolution} markdown />
										</div>
									) : selected.isBlocked ? (
										<p className="ticket-blocked-note">
											Waiting for {selected.blockedByIds.length} earlier decision
											{selected.blockedByIds.length === 1 ? "" : "s"}.
										</p>
									) : (
										<button
											type="button"
											onClick={() => {
												requestDraft(
													`In Wayfinder map “${map.title}” (${map.id}), regarding “${selectedLabel}”: `,
												);
												onActivePaneChange("chat");
											}}
										>
											Discuss this task
										</button>
									)}
									{selected.artifacts
										.filter((artifact) => artifact.role === "prototype")
										.map((artifact) => (
											<PrototypeViewer key={artifact.id} projectId={projectId} artifact={artifact} />
										))}
									{selected.artifacts
										.filter((artifact) => artifact.role === "research")
										.map((artifact) => (
											<button
												key={artifact.id}
												type="button"
												className="secondary prototype-open"
												disabled={loadingEvidence === artifact.artifactId}
												onClick={() => void readEvidence(artifact.artifactId)}
											>
												{loadingEvidence === artifact.artifactId
													? "Opening research…"
													: "Read research evidence"}
											</button>
										))}
									{evidence &&
										selected.artifacts.some((artifact) => artifact.artifactId === evidence.artifactId) && (
											<div className="ticket-evidence">
												<strong>{evidence.name.split("/").at(-1)}</strong>
												<MessageBody content={evidence.content} markdown />
											</div>
										)}
									{selected.status === "open" && selected.participation === "human" && !selected.isClaimed && (
										<button
											type="button"
											className="text-button wayfinding-delete"
											disabled={saving}
											onClick={() => void deleteQuestion()}
										>
											Delete question
										</button>
									)}
								</div>
							</section>
							{map.destinationRevisions.length > 1 && (
								<details className="destination-history">
									<summary>How the destination changed</summary>
									<ol>
										{map.destinationRevisions.map((revision) => (
											<li key={revision.id}>
												<strong>Revision {revision.revision}</strong>
												<span>{revision.destination}</span>
												{revision.reason && <small>{revision.reason}</small>}
											</li>
										))}
									</ol>
								</details>
							)}
							{(map.notYetSpecified || map.outOfScope) && (
								<div className="wayfinding-boundaries">
									{map.notYetSpecified && (
										<div>
											<strong>Not yet specified</strong>
											<MessageBody content={map.notYetSpecified} markdown />
										</div>
									)}
									{map.outOfScope && (
										<div>
											<strong>Outside this map</strong>
											<MessageBody content={map.outOfScope} markdown />
										</div>
									)}
								</div>
							)}
						</>
					) : (
						<p className="compact-empty-state">Choose a task from the selected map to see its details.</p>
					)}
				</aside>
			</div>
		</div>
	);
}

function ticketOrder(left: PortalWorkTicketDto, right: PortalWorkTicketDto) {
	const rank = (ticket: PortalWorkTicketDto) =>
		ticket.isFrontier
			? 0
			: ticket.status === "open" && ticket.isClaimed
				? 1
				: ticket.status === "open"
					? 2
					: ticket.status === "resolved"
						? 3
						: 4;
	return rank(left) - rank(right) || right.priority - left.priority || left.createdAt.localeCompare(right.createdAt);
}

function ticketState(ticket: PortalWorkTicketDto) {
	if (ticket.status === "resolved") return "decided";
	if (ticket.isFrontier) return "frontier";
	if (ticket.isClaimed) return "investigating";
	if (ticket.isBlocked) return "waiting";
	return "open";
}

function TicketNode({
	ticket,
	selected,
	onSelect,
}: {
	ticket: PortalWorkTicketDto;
	selected: boolean;
	onSelect: (id: string) => void;
}) {
	const state = ticketState(ticket);
	return (
		<button
			type="button"
			className={`atlas-node ${state}${selected ? " selected" : ""}`}
			aria-pressed={selected}
			onClick={() => onSelect(ticket.id)}
		>
			<i aria-hidden="true" />
			<RichText content={ticket.question ?? ticket.title} inline />
			<small>{state === "investigating" ? "Agent" : state}</small>
		</button>
	);
}

function PathNode({ ticket, active = false }: { ticket: PortalWorkTicketDto; active?: boolean }) {
	return (
		<span className={`path-node ${ticketState(ticket)}${active ? " active" : ""}`}>
			<RichText content={ticket.question ?? ticket.title} inline />
		</span>
	);
}
